import { useEffect, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  X,
  Calendar,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePIStore } from '../../store/usePIStore';
import SprintRolloverModal from './SprintRolloverModal';
import CreatePIModal from './CreatePIModal';
import type { ProgramIncrement, Sprint, WorkItem } from '../../types';

/**
 * Program Increments and their sprints.
 *
 * Lifted out of OrgAdminPanel when the rails were grouped: cadence drives
 * release generation, so PIs belong beside Releases under Release Management
 * rather than beside "create an organization". Behaviour is unchanged — this is
 * the same tree, reachable from a home that matches what it does.
 */
export default function ProgramIncrementsPanel() {
  const {
    pis,
    loading,
    fetchPIs,
    fetchAllSprints,
    deletePI,
    updateSprint,
    getSprintsForPI,
    startSprint,
    rolloverItems,
  } = usePIStore();

  const [showPIForm, setShowPIForm] = useState(false);
  const [editingPI, setEditingPI] = useState<ProgramIncrement | null>(null);
  const [expandedPIs, setExpandedPIs] = useState<Set<string>>(new Set());
  const [selectedSprint, setSelectedSprint] = useState<Sprint | null>(null);
  const [rolloverData, setRolloverData] = useState<{
    incompleteItems: WorkItem[];
    fromSprint: Sprint;
    toSprint: Sprint;
  } | null>(null);

  useEffect(() => {
    fetchPIs();
    fetchAllSprints();
  }, [fetchPIs, fetchAllSprints]);

  if (loading && pis.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-3 text-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Program Increments
        </span>
        <button
          onClick={() => {
            setEditingPI(null);
            setShowPIForm(true);
          }}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-accent"
          title="Add Program Increment"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* PI create/edit runs in a full wizard (cadence + calendar preview +
          project selection needs more room than this 320px panel). */}
      {showPIForm && (
        <CreatePIModal
          pi={editingPI}
          onClose={() => { setShowPIForm(false); setEditingPI(null); }}
        />
      )}

      {pis.length === 0 && !showPIForm && (
        <p className="text-gray-400 dark:text-gray-500 text-xs px-2">No program increments yet</p>
      )}

      <div className="space-y-0.5">
        {pis.map((pi) => {
          const isPIExpanded = expandedPIs.has(pi._id);
          const piSprints = getSprintsForPI(pi._id);
          const statusColor = pi.status === 'active' ? 'text-green-500' : pi.status === 'completed' ? 'text-blue-500' : 'text-gray-400';

          return (
            <div key={pi._id}>
              {/* PI row */}
              <div className="group flex items-center gap-1 px-1 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-750">
                <button
                  onClick={() => setExpandedPIs((prev) => {
                    const next = new Set(prev);
                    next.has(pi._id) ? next.delete(pi._id) : next.add(pi._id);
                    return next;
                  })}
                  className="p-0.5 text-gray-400"
                >
                  {isPIExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <Calendar size={14} className="text-purple-500 shrink-0" />
                <span className="flex-1 text-gray-800 dark:text-gray-200 truncate">
                  {pi.name}
                </span>
                <span className={`text-[10px] font-medium ${statusColor} mr-1`}>
                  {pi.status}
                </span>
                <span className="text-[10px] text-gray-400 mr-1">{piSprints.length}sp</span>
                <div className="hidden group-hover:flex items-center gap-0.5">
                  <button
                    onClick={() => {
                      setEditingPI(pi);
                      setShowPIForm(true);
                    }}
                    className="p-0.5 text-gray-400 hover:text-accent"
                    title="Edit PI"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={async () => {
                      if (!confirm(`Delete PI "${pi.name}" and all its sprints?`)) return;
                      try {
                        await deletePI(pi._id);
                        toast.success('PI deleted');
                      } catch {
                        toast.error('Delete failed');
                      }
                    }}
                    className="p-0.5 text-gray-400 hover:text-red-500"
                    title="Delete PI"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {/* Sprints under PI */}
              {isPIExpanded && (
                <div className="ml-5 border-l border-gray-200 dark:border-gray-700">
                  <div className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 px-1 py-0.5">
                    {pi.startDate} → {pi.endDate} · {pi.sprintLengthWeeks}wk sprints
                  </div>
                  {piSprints.map((sprint) => {
                    const sprintStatusColor = sprint.status === 'active' ? 'bg-green-500' : sprint.status === 'completed' ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600';
                    return (
                      <div
                        key={sprint._id}
                        className="group flex items-center gap-1 px-1 py-1.5 ml-2 rounded hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                        onClick={() => setSelectedSprint(selectedSprint?._id === sprint._id ? null : sprint)}
                      >
                        <Zap size={12} className="text-amber-500 shrink-0 ml-1" />
                        <span className="flex-1 text-gray-700 dark:text-gray-300 text-xs truncate">
                          {sprint.name}
                        </span>
                        <span className={`w-2 h-2 rounded-full ${sprintStatusColor} shrink-0`} title={sprint.status} />
                        <span className="text-[10px] text-gray-400">
                          {sprint.startDate?.slice(5)}
                        </span>
                      </div>
                    );
                  })}

                  {/* Sprint detail inline */}
                  {selectedSprint && piSprints.some((s) => s._id === selectedSprint._id) && (
                    <div className="ml-4 mr-1 my-1 p-2 bg-white dark:bg-gray-750 rounded border border-gray-200 dark:border-gray-600 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{selectedSprint.name}</span>
                        <button onClick={() => setSelectedSprint(null)} className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {selectedSprint.startDate} → {selectedSprint.endDate}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-gray-500 dark:text-gray-400">Status:</label>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          selectedSprint.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : selectedSprint.status === 'completed' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {selectedSprint.status}
                        </span>
                        {selectedSprint.status === 'planned' && (
                          <button
                            onClick={async () => {
                              try {
                                const result = await startSprint(selectedSprint._id);
                                if (result.incompleteItems.length > 0) {
                                  // Find the previous sprint for rollover modal
                                  const sprintsInPI = getSprintsForPI(selectedSprint.piId);
                                  const prevSprint = sprintsInPI.find((s) => s.sprintNumber === selectedSprint.sprintNumber - 1);
                                  if (prevSprint) {
                                    setRolloverData({
                                      incompleteItems: result.incompleteItems as WorkItem[],
                                      fromSprint: prevSprint,
                                      toSprint: result.sprint as Sprint,
                                    });
                                  }
                                }
                                setSelectedSprint({ ...selectedSprint, status: 'active' });
                                toast.success(`${selectedSprint.name} started`);
                              } catch (err: any) {
                                toast.error(err.message || 'Failed to start sprint');
                              }
                            }}
                            className="px-2 py-0.5 text-[10px] font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                          >
                            Start Sprint
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="text-gray-500 dark:text-gray-400">Goal:</label>
                        <input
                          type="text"
                          value={selectedSprint.goal || ''}
                          onChange={(e) => setSelectedSprint({ ...selectedSprint, goal: e.target.value })}
                          onBlur={async (e) => {
                            try {
                              await updateSprint(selectedSprint._id, { goal: e.target.value });
                            } catch {
                              toast.error('Failed to save goal');
                            }
                          }}
                          className="w-full mt-0.5 text-xs px-1 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none"
                          placeholder="Sprint goal..."
                        />
                      </div>
                    </div>
                  )}

                  {piSprints.length === 0 && (
                    <p className="text-xs text-gray-400 px-3 py-1 ml-2">No sprints</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sprint Rollover Modal */}
      {rolloverData && (
        <SprintRolloverModal
          incompleteItems={rolloverData.incompleteItems}
          fromSprint={rolloverData.fromSprint}
          toSprint={rolloverData.toSprint}
          onRollover={async (workItemIds) => {
            try {
              const count = await rolloverItems(rolloverData.toSprint._id, workItemIds);
              toast.success(`Rolled over ${count} item${count !== 1 ? 's' : ''}`);
              setRolloverData(null);
              await fetchAllSprints();
            } catch (err: any) {
              toast.error(err.message || 'Rollover failed');
            }
          }}
          onCancel={() => {
            setRolloverData(null);
            toast('Items remain in previous sprint. Move them manually.', { icon: 'ℹ️' });
          }}
        />
      )}
    </div>
  );
}
